import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import matchService from '../services/matchService';
import { useAuth } from '../context/AuthContext';
import '../styles/match-detail.css';

const MatchDetail = () => {
  const { id } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [relatedMatches, setRelatedMatches] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  // eslint-disable-next-line no-unused-vars
  const [_isJoining, _setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [userJoined, setUserJoined] = useState(false);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const fetchMatch = async () => {
      setLoading(true);
      try {
        const matchData = await matchService.getMatchById(id);
        setMatch(matchData);

        // Verificar si el usuario está inscrito en el partido
        if (user && matchData.players) {
          const isUserJoined = matchData.players.some((player) => player.user._id === user.id);
          setUserJoined(isUserJoined);
        }

        // Obtener partidos similares (misma categoría)
        const allMatches = await matchService.getMatches();
        const similar = allMatches
          .filter((m) => m._id !== id && m.category._id === matchData.category._id)
          .slice(0, 3);
        setRelatedMatches(similar);
      } catch (error) {
        console.error('Error fetching match:', error);
        setError('Error al cargar los datos del partido. Por favor, inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
  }, [id, user]);

  const handleJoinMatch = () => {
    if (!isAuthenticated) {
      setShowModal(true);
      return;
    }

    // Redirigir al usuario a la página de checkout
    navigate(`/match/${id}/checkout`);
  };

  const handleLeaveMatch = async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setIsLeaving(true);
      await matchService.leaveMatch(id);

      // Recargar los datos del partido
      const matchData = await matchService.getMatchById(id);
      setMatch(matchData);
      setUserJoined(false);

      // Mostrar mensaje de éxito
      setError({ type: 'success', message: 'Has abandonado el partido correctamente' });

      // Ocultar el mensaje después de 3 segundos
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (error) {
      console.error('Error leaving match:', error);
      setError({
        type: 'error',
        message:
          'Error al abandonar el partido: ' +
          ((error.response && error.response.data && error.response.data.message) || error.message),
      });
    } finally {
      setIsLeaving(false);
    }
  };

  const handleAddToCalendar = () => {
    if (!match) return;

    // Crear evento para Google Calendar
    const startDate = new Date(match.date);
    const endDate = new Date(startDate);
    endDate.setHours(endDate.getHours() + 1.5); // Asumimos que los partidos duran 1.5 horas

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Partido de fútbol: ${match.category.name}`)}&dates=${startDate.toISOString().replace(/-|:|\.\d+/g, '')}/${endDate.toISOString().replace(/-|:|\.\d+/g, '')}&details=${encodeURIComponent(`Partido en ${match.venue.name}`)}&location=${encodeURIComponent(`${match.venue.address.street}, ${match.venue.address.city}`)}&sf=true&output=xml`;

    window.open(googleCalendarUrl, '_blank');
  };

  const handleShareMatch = () => {
    if (!match) return;

    if (navigator.share) {
      navigator
        .share({
          title: `Partido de fútbol: ${match.category.name}`,
          text: `¡Únete a este partido de fútbol en ${match.venue.name}!`,
          url: window.location.href,
        })
        .catch((error) => console.error('Error sharing:', error));
    } else {
      // Fallback para navegadores que no soportan Web Share API
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setError({ type: 'success', message: 'Enlace copiado al portapapeles' });
          setTimeout(() => setError(null), 3000);
        })
        .catch((err) => {
          console.error('Error copying to clipboard:', err);
          setError({ type: 'error', message: 'Error al copiar el enlace' });
        });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const closeModal = () => {
    setShowModal(false);
  };

  // eslint-disable-next-line no-unused-vars
  const getTeamPlayers = (team) => {
    if (!match || !match.players) return [];
    return match.players.filter((player) => player.team === team);
  };

  if (loading) {
    return <div className="loading">Cargando detalles del partido...</div>;
  }

  if (error && typeof error === 'string') {
    return <div className="error">{error}</div>;
  }

  if (!match) {
    return <div className="error">No se encontró el partido</div>;
  }

  const matchDate = new Date(match.date);

  return (
    <section className="match-detail-section">
      <div className="container">
        <div className="match-detail-header">
          <div className="match-detail-info">
            <h1>
              Partido de <span id="match-category">{match.category.name}</span>
            </h1>
            <div className="match-detail-meta">
              <div className="match-meta-item">
                <span className="material-icons">event</span>
                <span id="match-date">
                  {matchDate.toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}{' '}
                  - {match.startTime}
                </span>
              </div>
              <div className="match-meta-item">
                <span className="material-icons">location_on</span>
                <span id="match-location">{match.venue.name}</span>
              </div>
              <div className="match-meta-item">
                <span className="material-icons">euro</span>
                <span id="match-price">{match.price},00 €</span>
              </div>
              <div className="match-meta-item">
                <span className="material-icons">people</span>
                <span id="match-spots">
                  {(match.players || []).length}/{match.maxPlayers} plazas ocupadas
                </span>
              </div>
            </div>
          </div>
          <div className="match-detail-actions">
            {error && typeof error === 'object' && (
              <div
                className={`alert ${error.type === 'success' ? 'alert-success' : 'alert-error'}`}
              >
                {error.message}
              </div>
            )}

            {isAuthenticated && !userJoined && (
              <button
                id="register-button"
                className="btn btn-primary"
                onClick={handleJoinMatch}
                disabled={(match.players || []).length >= match.maxPlayers}
              >
                Inscribirse
              </button>
            )}

            {isAuthenticated && userJoined && (
              <button
                id="leave-button"
                className="btn btn-danger"
                onClick={handleLeaveMatch}
                disabled={isLeaving}
              >
                {isLeaving ? 'Abandonando...' : 'Abandonar partido'}
              </button>
            )}

            {!isAuthenticated && (
              <button id="register-button" className="btn btn-primary" onClick={handleJoinMatch}>
                Inscribirse
              </button>
            )}

            <button id="share-button" className="btn btn-secondary" onClick={handleShareMatch}>
              <span className="material-icons">share</span> Compartir
            </button>

            <button
              id="calendar-button"
              className="btn btn-secondary"
              onClick={handleAddToCalendar}
            >
              <span className="material-icons">event</span> Añadir al calendario
            </button>
          </div>
        </div>

        <div className="match-detail-tabs">
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              <span className="material-icons">info</span> Información
            </button>
            <button
              className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
              onClick={() => setActiveTab('location')}
            >
              <span className="material-icons">location_on</span> Ubicación
            </button>
            <button
              className={`tab-button ${activeTab === 'players' ? 'active' : ''}`}
              onClick={() => setActiveTab('players')}
            >
              <span className="material-icons">people</span> Jugadores
            </button>
          </div>

          <div className="tabs-content">
            {/* Pestaña de Información */}
            <div className={`tab-pane ${activeTab === 'info' ? 'active' : ''}`}>
              <div className="match-detail-description">
                <h2>Descripción</h2>
                <p>
                  {match.description ||
                    'Partido amistoso para la categoría '.concat(
                      match.category.name,
                      '. Este partido está diseñado para que los jugadores puedan practicar sus habilidades en un ambiente divertido y competitivo. Los equipos se formarán al inicio del partido para asegurar un equilibrio entre los participantes.',
                    )}
                </p>
                <p>
                  Recomendamos llegar 15 minutos antes para el calentamiento. No olvides traer ropa
                  deportiva adecuada, botas de fútbol y una botella de agua.
                </p>

                <h3>Reglas del partido</h3>
                <ul>
                  <li>Duración: 2 tiempos de 25 minutos</li>
                  <li>Descanso: 10 minutos</li>
                  <li>Tamaño del campo: Reducido</li>
                  <li>
                    Número de jugadores: {match.maxPlayers / 2} vs {match.maxPlayers / 2}
                  </li>
                  <li>Se aplicarán las reglas oficiales de la categoría {match.category.name}</li>
                </ul>

                <h3>Información de la sede</h3>
                <div className="venue-info">
                  <div className="venue-image">
                    <img
                      src={match.venue.mainImage || '/img/default-venue.jpg'}
                      alt={match.venue.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/img/default-venue.jpg';
                      }}
                    />
                  </div>
                  <div className="venue-details">
                    <p>
                      <strong>Tipo de superficie:</strong> {match.venue.surface === 'natural' 
                        ? 'Césped natural' 
                        : match.venue.surface === 'artificial' 
                          ? 'Césped artificial' 
                          : 'Superficie híbrida'}
                    </p>
                    <p>
                      <strong>Tamaño:</strong> {match.venue.size === 'small' 
                        ? 'Pequeño' 
                        : match.venue.size === 'medium' 
                          ? 'Mediano' 
                          : 'Completo'}
                    </p>
                    <p>
                      <strong>Instalaciones:</strong>{' '}
                      {[
                        match.venue.features?.parking && 'Parking',
                        match.venue.features?.changingRooms && 'Vestuarios',
                        match.venue.features?.lighting && 'Iluminación',
                        match.venue.features?.cafeteria && 'Cafetería',
                        match.venue.features?.stands && 'Gradas',
                      ].filter(Boolean).join(', ') || 'No hay información disponible'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pestaña de Ubicación */}
            <div className={`tab-pane ${activeTab === 'location' ? 'active' : ''}`}>
              <div className="match-detail-map">
                <h2>Ubicación</h2>
                <div className="map-container">
                  <img src="/img/map-detail.svg" alt="Mapa del campo" className="map-image" />
                </div>
                <p className="map-address">
                  <strong>{match.venue.name}</strong>
                  <br />
                  {match.venue.address.street}
                  <br />
                  {match.venue.address.postalCode} {match.venue.address.city}
                </p>
                <div className="map-actions">
                  <a
                    href={`https://maps.google.com/?q=${match.venue.address.street},${match.venue.address.city}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary btn-sm"
                  >
                    <span className="material-icons">map</span> Ver en Google Maps
                  </a>
                  <a
                    href={`https://www.waze.com/ul?ll=${match.venue.lat || 40.416775},${match.venue.lng || -3.70379}&navigate=yes`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary btn-sm"
                  >
                    <span className="material-icons">directions_car</span> Navegar con Waze
                  </a>
                </div>

                <h3>Cómo llegar</h3>
                <div className="directions">
                  <div className="direction-item">
                    <span className="material-icons">directions_car</span>
                    <div>
                      <h4>En coche</h4>
                      <p>
                        Hay aparcamiento disponible en las inmediaciones del campo. Se recomienda
                        llegar con 15 minutos de antelación para encontrar aparcamiento.
                      </p>
                    </div>
                  </div>
                  <div className="direction-item">
                    <span className="material-icons">directions_bus</span>
                    <div>
                      <h4>En transporte público</h4>
                      <p>Líneas de autobús: 27, 45, 63 (parada &apos;Campo Municipal&apos;)</p>
                      <p>Metro: Línea 4, estación &apos;Estadio&apos; (a 10 minutos a pie)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pestaña de Jugadores */}
            <div className={`tab-pane ${activeTab === 'players' ? 'active' : ''}`}>
              <div className="match-detail-players">
                <h2>
                  Jugadores Inscritos ({(match.players || []).length}/{match.maxPlayers})
                </h2>

                <div className="players-status">
                  <div className="status-bar">
                    <div
                      className="status-fill"
                      style={{ width: `${((match.players || []).length / match.maxPlayers) * 100}%` }}
                    ></div>
                  </div>
                  <p className="status-text">
                    {(match.players || []).length >= match.maxPlayers
                      ? 'Partido completo'
                      : `${match.maxPlayers - (match.players || []).length} plazas disponibles`}
                  </p>
                </div>

                <div className="players-grid">
                  {(match.players || []).length === 0 ? (
                    <p className="no-players">
                      No hay jugadores inscritos todavía. ¡Sé el primero en unirte!
                    </p>
                  ) : (
                    (match.players || []).map((player) => (
                      <div className="player-card" key={player.user._id}>
                        <img
                          src={player.user.avatar || '/img/default-avatar.svg'}
                          alt={player.user.name}
                          className="player-image"
                        />
                        <div className="player-info">
                          <h3>{player.user.name}</h3>
                          <p>{player.position || 'Jugador'}</p>
                          {player.team && (
                            <span className={`team-badge team-${player.team}`}>{player.team}</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partidos relacionados */}
        <div className="match-detail-related">
          <h2>Otros Partidos Similares</h2>
          <div className="related-matches">
            {relatedMatches.length === 0 ? (
              <p>No hay partidos similares disponibles.</p>
            ) : (
              relatedMatches.map((relatedMatch) => (
                <div className="match-card" key={relatedMatch._id}>
                  <div className="match-date">
                    {new Date(relatedMatch.date).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  <div className="match-category">{relatedMatch.category.name}</div>
                  <div className="match-location">{relatedMatch.venue.name}</div>
                  <div className="match-spots">
                    Plazas: {(relatedMatch.players || []).length}/{relatedMatch.maxPlayers}
                  </div>
                  <Link to={`/match/${relatedMatch._id}`} className="btn btn-primary">
                    Ver Detalles
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal de Inscripción */}
      <div id="registration-modal" className={`modal ${showModal ? 'open' : ''}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2>Inscripción al Partido</h2>
            <span className="close-modal" onClick={() => setShowModal(false)}>
              &times;
            </span>
          </div>
          <div className="modal-body">
            <p>
              Para inscribirte a este partido, debes iniciar sesión o registrarte en la plataforma.
            </p>
            <div className="modal-actions">
              <Link to="/login" className="btn btn-primary">
                Iniciar Sesión
              </Link>
              <Link to="/register" className="btn btn-secondary">
                Registrarse
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MatchDetail;
